import { useEffect, useRef, useState, type PointerEvent, type ReactNode } from "react";

const MARC_STILL = "/cdn/sim/holiday-call/img/marc-weber.webp";
const MARC_TALK = "/cdn/sim/holiday-call/clips/marc-04.mp4";
const MARC_ORIG = "/cdn/sim/holiday-call/recon/marc-original.mp4";
const MARC_CLONE = "/cdn/sim/holiday-call/recon/marc-clone-test-01.mp3";
const SARAH_TALK = "/cdn/sim/holiday-call/clips/sarah-01.mp4";

export function VerifyLesson({
  kicker,
  title,
  observe,
  interpret,
  control,
  takeaway,
  children,
}: {
  kicker: string;
  title: string;
  observe: string;
  interpret: string;
  control: string;
  takeaway: string;
  children?: ReactNode;
}) {
  return (
    <div className="hv-lesson">
      <header className="hv-lesson-head"><small>{kicker}</small><h3>{title}</h3></header>
      <div className="hv-lesson-layout">
        <div className="hv-lesson-demo">{children}</div>
        <div className="hv-lesson-copy">
          <ol className="hv-steps">
            <li><b>Observe</b><span>{observe}</span></li>
            <li><b>Interpret carefully</b><span>{interpret}</span></li>
            <li><b>Control</b><span>{control}</span></li>
          </ol>
          <p className="hv-takeaway">{takeaway}</p>
        </div>
      </div>
    </div>
  );
}

export function PhotoToFakeReveal({ stillUrl, clipUrl }: { stillUrl?: string; clipUrl?: string }) {
  const still = stillUrl || MARC_STILL;
  const clip = clipUrl || MARC_TALK;
  const [phase, setPhase] = useState<"still" | "scan" | "live">("still");
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    const scan = window.setTimeout(() => setPhase("scan"), 700);
    const live = window.setTimeout(() => {
      setPhase("live");
      void videoRef.current?.play().catch(() => undefined);
    }, 2100);
    return () => {
      window.clearTimeout(scan);
      window.clearTimeout(live);
    };
  }, [clip]);

  return (
    <div className={`hv-reveal hv-reveal--${phase}`}>
      <div className="hv-reveal-still">
        <img src={still} alt="Public still used to build the talking avatar" />
        <span>One public still</span>
      </div>
      <div className="hv-reveal-arrow" aria-hidden>
        <i />
        <em>minutes later</em>
      </div>
      <div className="hv-reveal-live">
        <video ref={videoRef} src={clip} muted playsInline loop />
        <span>Talking avatar on the call</span>
      </div>
      <div className="hv-reveal-scan" aria-hidden />
    </div>
  );
}

export function ForensicLoupe({ src, hint }: { src: string; hint: string }) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const magRef = useRef<HTMLVideoElement>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  const [paused, setPaused] = useState(false);
  const [lens, setLens] = useState({ x: 62, y: 48 });
  const MAG = 2.3;
  const LENS = 0.28;
  const innerPct = (MAG / LENS) * 100;

  const syncMag = (time?: number) => {
    const a = videoRef.current;
    const b = magRef.current;
    if (!a || !b) return;
    const t = time ?? a.currentTime;
    if (Math.abs(b.currentTime - t) > 0.04) b.currentTime = t;
    if (a.paused !== b.paused) {
      if (a.paused) b.pause();
      else void b.play().catch(() => undefined);
    }
  };

  const setPlaying = (play: boolean) => {
    const el = videoRef.current;
    if (!el) return;
    if (play) {
      void el.play().catch(() => undefined);
      setPaused(false);
    } else {
      el.pause();
      setPaused(true);
    }
    syncMag();
  };

  const step = (dir: number) => {
    const el = videoRef.current;
    if (!el) return;
    el.pause();
    setPaused(true);
    const next = Math.max(0, Math.min((el.duration || 8) - 0.04, el.currentTime + dir * 0.04));
    el.currentTime = next;
    syncMag(next);
  };

  const onMove = (e: PointerEvent<HTMLDivElement>) => {
    const box = wrapRef.current?.getBoundingClientRect();
    if (!box) return;
    const x = ((e.clientX - box.left) / box.width) * 100;
    const y = ((e.clientY - box.top) / box.height) * 100;
    setLens({ x: Math.max(8, Math.min(92, x)), y: Math.max(8, Math.min(92, y)) });
  };

  return (
    <div className="hv-loupe">
      <div
        ref={wrapRef}
        className="hv-loupe-stage"
        onPointerMove={onMove}
        onClick={() => setPlaying(paused)}
      >
        <video
          ref={videoRef}
          src={src}
          muted
          playsInline
          loop
          autoPlay
          onTimeUpdate={() => syncMag()}
        />
        <div className="hv-loupe-lens" style={{ left: `${lens.x}%`, top: `${lens.y}%` }}>
          <video
            ref={magRef}
            src={src}
            muted
            playsInline
            loop
            aria-hidden
            style={{
              width: `${innerPct}%`,
              height: `${innerPct}%`,
              left: `${50 - (lens.x / 100) * innerPct}%`,
              top: `${50 - (lens.y / 100) * innerPct}%`,
            }}
          />
        </div>
      </div>
      <div className="hv-loupe-bar">
        <button type="button" onClick={() => step(-1)}>-1 frame</button>
        <button type="button" onClick={() => setPlaying(paused)}>
          {paused ? "Play" : "Pause"}
        </button>
        <button type="button" onClick={() => step(1)}>+1 frame</button>
        <span>{hint}</span>
      </div>
    </div>
  );
}

export function VoiceClonePair() {
  const origRef = useRef<HTMLVideoElement>(null);
  const cloneRef = useRef<HTMLAudioElement>(null);
  const [playing, setPlaying] = useState<"orig" | "clone" | null>(null);

  const stop = () => {
    origRef.current?.pause();
    cloneRef.current?.pause();
  };

  return (
    <div className="hv-voicepair">
      <video ref={origRef} src={MARC_ORIG} playsInline onEnded={() => setPlaying(null)} hidden />
      <audio ref={cloneRef} src={MARC_CLONE} onEnded={() => setPlaying(null)} />
      <button
        type="button"
        className={playing === "orig" ? "on" : ""}
        onClick={() => {
          stop();
          setPlaying("orig");
          if (origRef.current) {
            origRef.current.currentTime = 0;
            void origRef.current.play();
          }
        }}
      >
        {playing === "orig" ? "Playing original…" : "Play original interview"}
      </button>
      <button
        type="button"
        className={playing === "clone" ? "on clone" : "clone"}
        onClick={() => {
          stop();
          setPlaying("clone");
          if (cloneRef.current) {
            cloneRef.current.currentTime = 0;
            void cloneRef.current.play();
          }
        }}
      >
        {playing === "clone" ? "Playing clone…" : "Play cloned sentence"}
      </button>
    </div>
  );
}

export function HkFraudCinematic() {
  const [step, setStep] = useState(0);
  useEffect(() => {
    const timers = [700, 1400, 2100, 2800, 3600].map((ms, i) =>
      window.setTimeout(() => setStep(i + 1), ms),
    );
    return () => timers.forEach(window.clearTimeout);
  }, []);
  const people = ["CFO", "Legal", "HR", "Colleague"];
  return (
    <div className="hv-hk">
      <div className="hv-hk-grid">
        {people.map((role, i) => (
          <article key={role} className={step > i ? "fake" : ""}>
            <span>{role}</span>
            <b>{step > i ? "SYNTHETIC" : "On the call"}</b>
          </article>
        ))}
      </div>
      <div className={`hv-hk-counter${step >= 5 ? " on" : ""}`}>
        <small>VIDEO-MEETING FRAUD · HONG KONG 2024</small>
        <strong>{step >= 5 ? "USD 25 million" : "Transfers in progress…"}</strong>
        <p>Every participant except the victim was fake. The failure was process — not spotting teeth.</p>
      </div>
    </div>
  );
}

export function ToolArt({ id }: { id: string }) {
  if (id === "marc_video") {
    return (
      <svg className="hm-tool-art" viewBox="0 0 64 40" aria-hidden>
        <rect x="4" y="6" width="36" height="28" rx="3" fill="none" stroke="currentColor" strokeWidth="1.6" />
        <circle cx="22" cy="18" r="6" fill="none" stroke="currentColor" />
        <path d="M12 30c3-5 17-5 20 0" fill="none" stroke="currentColor" />
        <rect x="42" y="12" width="16" height="16" rx="2" fill="currentColor" opacity=".35" />
        <path d="M46 20h8M50 16v8" stroke="#7ee0cc" strokeWidth="1.6" />
      </svg>
    );
  }
  if (id === "sarah_video") {
    return (
      <svg className="hm-tool-art" viewBox="0 0 64 40" aria-hidden>
        <circle cx="32" cy="16" r="11" fill="none" stroke="currentColor" strokeWidth="1.6" />
        <path d="M24 16h4M36 16h4" stroke="currentColor" />
        <path d="M24 22c3 5 13 5 16 0" fill="none" stroke="currentColor" strokeWidth="1.6" />
        <path d="M26 23.5 28 26 30 23 32 27 34 23 36 26 38 23.5" fill="none" stroke="#7ee0cc" strokeWidth="1.2" />
      </svg>
    );
  }
  if (id === "question") {
    return (
      <svg className="hm-tool-art" viewBox="0 0 64 40" aria-hidden>
        <path d="M18 28c0-8 28-8 28 0v4H18z" fill="none" stroke="currentColor" strokeWidth="1.6" />
        <circle cx="32" cy="14" r="8" fill="none" stroke="currentColor" strokeWidth="1.6" />
        <text x="32" y="18" textAnchor="middle" fontSize="11" fill="currentColor">?</text>
      </svg>
    );
  }
  if (id === "linkedin") {
    return (
      <svg className="hm-tool-art" viewBox="0 0 64 40" aria-hidden>
        <rect x="10" y="8" width="44" height="24" rx="3" fill="none" stroke="currentColor" strokeWidth="1.6" />
        <circle cx="22" cy="18" r="4" />
        <path d="M30 14h18M30 20h14M30 26h10" stroke="currentColor" />
      </svg>
    );
  }
  if (id === "calendar") {
    return (
      <svg className="hm-tool-art" viewBox="0 0 64 40" aria-hidden>
        <rect x="14" y="8" width="36" height="26" rx="3" fill="none" stroke="currentColor" strokeWidth="1.6" />
        <path d="M14 16h36M24 8v6M40 8v6" stroke="currentColor" />
        <rect x="22" y="20" width="8" height="8" fill="currentColor" opacity=".45" />
      </svg>
    );
  }
  if (id === "callback") {
    return (
      <svg className="hm-tool-art" viewBox="0 0 64 40" aria-hidden>
        <rect x="24" y="4" width="16" height="32" rx="3" fill="none" stroke="currentColor" strokeWidth="1.6" />
        <circle cx="32" cy="30" r="1.6" />
        <path d="M8 20h12M44 20h12" stroke="#7ee0cc" strokeWidth="1.8" />
      </svg>
    );
  }
  return (
    <svg className="hm-tool-art" viewBox="0 0 64 40" aria-hidden>
      <path d="M12 28h40l-4-16H16z" fill="none" stroke="currentColor" strokeWidth="1.6" />
      <path d="M20 28v6h24v-6" stroke="currentColor" />
      <text x="32" y="22" textAnchor="middle" fontSize="9" fill="currentColor">$</text>
    </svg>
  );
}

export function MarcInspect({ stillUrl, clipUrl }: { stillUrl?: string; clipUrl?: string }) {
  return (
    <VerifyLesson
      kicker="VISUAL INSPECTION · MARC"
      title="One photo is all it took"
      observe="A single public still was enough to generate a talking face. Drag the loupe over the mouth and jaw while it moves."
      interpret="Photo-driven avatars invent teeth, mouth interiors and motion that were never in the still. You may see a lip mask, unstable teeth, or a shimmer at the face edge. Compression and a bad hotel network can imitate the same clues. None of this authenticates anyone."
      control="Do not approve a payment because the face 'looks like Marc'. Identity is verified out of band, on a number already in the company directory."
      takeaway="A face on a call is a performance, not a passport."
    >
      <PhotoToFakeReveal stillUrl={stillUrl} clipUrl={clipUrl || MARC_TALK} />
      <ForensicLoupe src={clipUrl || MARC_TALK} hint="Drag the lens. Pause and step a frame. Look at teeth and the lip edge — then remember this is not proof." />
    </VerifyLesson>
  );
}

export function SarahInspect({ clipUrl, stillUrl }: { clipUrl?: string; stillUrl?: string }) {
  return (
    <VerifyLesson
      kicker="VISUAL INSPECTION · SARAH"
      title="The mouth and the shoulders do not agree"
      observe="Pause and step a frame. The same tooth stretches, then the whole smile resizes — one frame can look like a pointed canine, the next like a wider, bigger set. Her shoulders bob up and down while the rest of the torso stays frozen."
      interpret="Talking avatars invent teeth and mouth interiors that were never in the still. Body motion often lives only in the head and shoulders. A live person does not keep a perfectly static torso while the shoulders pump. Compression can imitate a little of this. None of it authenticates anyone."
      control="Treat those tells as a reason to slow down, not as a detector. Verify the person through a channel you already trust."
      takeaway="If you needed a perfect fake, you already lost. Use process."
    >
      {stillUrl && <img className="hv-still-chip" src={stillUrl} alt="" />}
      <ForensicLoupe src={clipUrl || SARAH_TALK} hint="Look at the teeth, then the shoulders versus the rest of the body. Then close this card." />
    </VerifyLesson>
  );
}

export function QuestionInspect({ answer }: { answer: string }) {
  return (
    <VerifyLesson
      kicker="PERSONAL KNOWLEDGE"
      title="A familiar voice can still be generated"
      observe="Play the original interview, then the cloned sentence. Same voice. Different words — including a payment instruction Marc never recorded."
      interpret={`Elena's "personal" question about Da Marco can be answered from public posts, scraped chat, or a language model. A correct answer plus a cloned voice is still not Marc.`}
      control="Never treat in-call knowledge or a familiar voice as authentication. Verify independently, outside this meeting."
      takeaway="Voices clone from seconds of audio. Questions leak from the public web."
    >
      <blockquote className="hv-q">“What restaurant did we go to after the board meeting last month?”</blockquote>
      <p className="hm-answer">Marc: “{answer}”</p>
      <VoiceClonePair />
    </VerifyLesson>
  );
}

export function LinkedInInspect({
  people,
}: {
  people: { id: string; name: string; role: string; avatarUrl?: string }[];
}) {
  return (
    <VerifyLesson
      kicker="IDENTITY LOOKUP"
      title="The profiles are real. The call is not."
      observe="These people exist at Aureon. Their photos, titles and networks are public."
      interpret="A real LinkedIn page proves that a person exists, not that they control this meeting. Attackers copy the page onto a synthetic face."
      control="Do not use the meeting chat or a number offered on the call. Open the corporate directory yourself."
      takeaway="Existence is not presence."
    >
      <div className="hm-id-list">
        {people.map((p) => (
          <div key={p.id}>
            <span>{p.avatarUrl ? <img src={p.avatarUrl} alt="" /> : p.name[0]}</span>
            <strong>{p.name}</strong>
            <small>{p.role} · Identity exists</small>
          </div>
        ))}
      </div>
    </VerifyLesson>
  );
}

export function CalendarInspect({ warning }: { warning: string }) {
  return (
    <VerifyLesson
      kicker="COMPANY CALENDAR"
      title="Context can contradict — and still be incomplete"
      observe={`Marc's calendar shows: ${warning}. That clashes with an urgent Horizon payment call.`}
      interpret="A clash is a useful suspicion, not identity verification. Calendars are stale, delegated, or wrong. An attacker also cannot be disproved by a free slot."
      control="Use the contradiction to pause. Then verify through a stored number and the payment workflow."
      takeaway="A calendar is a clue. It is not a callback."
    >
      <div className="hm-calendar">
        <b>16:00–17:00</b>
        <span>{warning}</span>
      </div>
    </VerifyLesson>
  );
}

export function CallbackInspect({ reply }: { reply: string }) {
  return (
    <VerifyLesson
      kicker="INDEPENDENT CHANNEL"
      title="Call the number you already had"
      observe="Elena dials Marc's stored corporate mobile — not a number from the meeting invite."
      interpret="The real Marc is in Zurich and is not on this call. That single fact collapses every synthetic colleague on the grid."
      control="Always originate the callback from the company directory. Never from a link, chat, or number supplied in the suspicious meeting."
      takeaway="The channel you already trusted is the control."
    >
      <div className="hm-phone-call">
        <span>MW</span>
        <strong>Marc Weber · Corporate mobile</strong>
        <i>Connected</i>
      </div>
      <blockquote>{reply}</blockquote>
      <div className="hm-exposed">The meeting is fake.</div>
    </VerifyLesson>
  );
}

export function TreasuryInspect() {
  return (
    <VerifyLesson
      kicker="THE WHOLE ROOM"
      title="A convincing meeting can still be one attacker"
      observe="You just sat in a call with Marc, Sarah and Julian. They backed each other up. That social proof was the product — several familiar faces on one grid."
      interpret="In a 2024 Hong Kong video-meeting fraud, a finance employee authorized about USD 25 million after every other participant on the call was synthetic. The victim did not fail a glasses test. They let the room override process."
      control="Do not treat a group of familiar faces as confirmation. A payment still has to exist in the workflow you already use."
      takeaway="The grid can be entirely generated. Process is what is left."
    >
      <HkFraudCinematic />
    </VerifyLesson>
  );
}
